/**
 * Types for usePlayBlocks hook
 *
 * Hook-specific types for managing play state with React Query.
 */

import type {
	ResolvedBlock,
	FormationBlock,
	ConceptBlock,
	ConceptReferenceBlock,
} from '@/types/blocks'
import type { Drawing } from '@/types/Drawing.types'
import type { DrawingState, Tool } from '@/types/Play.types'
import type { HashAlignment } from '@/types/Field.types'
import type { Player } from '@/types/Player.types'
import type { PlayCallChip } from '@/types/BlockProperties'

// Re-export imported types for backwards compatibility
export type { Drawing, DrawingState, Tool, HashAlignment, Player }

// ========== Drawing State ==========

/**
 * Default drawing state values
 */
export const DEFAULT_DRAWING_STATE: DrawingState = {
	tool: 'select' as Tool,
	color: '#000000',
	brushSize: 7,
	lineStyle: 'solid',
	lineEnd: 'none',
	pathMode: 'curve',
	eraseSize: 20,
	snapThreshold: 15,
}

/**
 * Default hash alignment
 */
export const DEFAULT_HASH_ALIGNMENT: HashAlignment = 'middle'

// ========== Hook Options ==========

/**
 * Options for the usePlayBlocks hook
 */
export interface UsePlayBlocksOptions {
	/** The play block ID to fetch (null for new plays) */
	playBlockId: string | null
	/** The playbook ID for context */
	playbookId: string
	/** Whether to enable fetching (default: true) */
	enabled?: boolean
}

// ========== Mutations ==========

/**
 * Placeholder type for block mutations (Phase 2)
 */
export interface PlayBlockMutations {
	// Player mutations
	addPlayer: (player: Player) => Promise<string>
	updatePlayer: (playerId: string, updates: Partial<Player>) => Promise<void>
	deletePlayer: (playerId: string) => Promise<void>

	// Drawing mutations
	addDrawing: (drawing: Drawing) => Promise<void>
	updateDrawing: (drawingId: string, updates: Partial<Drawing>) => Promise<void>
	deleteDrawing: (drawingId: string) => Promise<void>

	// Control point mutations
	updateControlPoint: (drawingId: string, pointId: string, x: number, y: number) => Promise<void>

	// Drawing-player link mutations
	linkDrawingToPlayer: (drawingId: string, pointId: string, playerId: string) => Promise<void>
	unlinkDrawingFromPlayer: (drawingId: string) => Promise<void>

	// Play metadata
	updatePlayName: (name: string) => Promise<void>
	updatePlayNotes: (notes: string | null) => Promise<void>
	updateHashPosition: (position: HashAlignment) => Promise<void>
	updateCallChips: (chips: PlayCallChip[]) => Promise<void>

	// Formation/Concept references
	applyFormation: (formationId: string) => Promise<void>
	removeFormation: () => Promise<void>
	applyConcept: (conceptId: string) => Promise<void>
	removeConceptReference: (referenceId: string) => Promise<void>
}

// ========== Hook Return Type ==========

/**
 * Return type for the usePlayBlocks hook
 */
export interface UsePlayBlocksReturn {
	// ========== Server State (React Query) ==========
	/** The resolved block data from server */
	resolvedBlock: ResolvedBlock | null
	/** Whether the play is currently loading */
	isLoading: boolean
	/** Error from fetching, if any */
	error: Error | null

	// ========== Transformed for Canvas (via adapters - Phase 3) ==========
	/** Players transformed for canvas rendering */
	players: Player[]
	/** Drawings transformed for canvas rendering */
	drawings: Drawing[]

	// ========== Local UI State (not persisted) ==========
	/** Current drawing tool state */
	drawingState: DrawingState
	/** Update drawing state */
	setDrawingState: (updates: Partial<DrawingState>) => void
	/** Current hash alignment */
	hashAlignment: HashAlignment
	/** Update hash alignment */
	setHashAlignment: (alignment: HashAlignment) => void

	// ========== Mutations (auto-save) - placeholder, wired in Phase 2 ==========
	/** Block mutation handlers */
	mutations: PlayBlockMutations

	// ========== Undo/Redo - placeholder, wired in Phase 4 ==========
	/** Undo last action */
	undo: () => Promise<void>
	/** Redo last undone action */
	redo: () => Promise<void>
	/** Whether undo is available */
	canUndo: boolean
	/** Whether redo is available */
	canRedo: boolean

	// ========== Applied References (for chip UI) ==========
	/** The formation currently applied to this play */
	appliedFormation: FormationBlock | null
	/** The concepts currently applied to this play */
	appliedConcepts: Array<{
		reference: ConceptReferenceBlock
		concept: ConceptBlock
	}>

	// ========== Refresh ==========
	/** Manually refetch the play data */
	refetch: () => void
}
